import React, { useRef, useEffect, useMemo, useState } from "react";
import { Box, Typography, Chip } from "@mui/material";
import {
  ALL_CATEGORIES_LABEL,
  PRODUCT_CATEGORIES,
} from "../config/productCategories";
import { brandColors } from "../config/brandTheme";
import { getCategoryImageSrc, getLocalCategoryImageSrc } from "../utils/categoryImages";
import { fetchSalesbeezCategoryImages } from "../services/SalesbeezCategoryService";

const TILE_BG = "#F5F1E6";

const CategoryIconStrip = ({
  selectedCategory,
  onSelectCategory,
  itemCountByCategory = {},
  categoryImageOverrides = {},
  primaryBlue,
}) => {
  const scrollRef = useRef(null);
  const selectedRef = useRef(null);
  const [brokenImages, setBrokenImages] = useState({});
  const [salesbeezImages, setSalesbeezImages] = useState({});

  useEffect(() => {
    let active = true;
    fetchSalesbeezCategoryImages().then((map) => {
      if (active) setSalesbeezImages(map || {});
    });
    return () => {
      active = false;
    };
  }, []);

  const stripItems = useMemo(() => {
    const withCounts = PRODUCT_CATEGORIES.map((c) => ({
      name: c.name,
      shortLabel: c.shortLabel,
      count: itemCountByCategory[c.name] || 0,
    }));
    const available = withCounts.filter((c) => c.count > 0);
    const comingSoon = withCounts.filter((c) => c.count === 0);
    return [
      { name: ALL_CATEGORIES_LABEL, shortLabel: "All" },
      ...available,
      ...comingSoon,
    ];
  }, [itemCountByCategory]);

  useEffect(() => {
    if (selectedRef.current && scrollRef.current) {
      selectedRef.current.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
  }, [selectedCategory]);

  const handleImageError = (key) => {
    setBrokenImages((prev) => ({ ...prev, [key]: true }));
  };

  return (
    <Box
      component="section"
      sx={{
        bgcolor: "#FFFFFF",
        borderBottom: `1px solid rgba(11, 28, 72, 0.08)`,
        py: { xs: 2, sm: 2.5 },
      }}
    >
      <Box sx={{ maxWidth: "lg", mx: "auto", px: { xs: 2, sm: 3 } }}>
        <Typography
          component="h2"
          sx={{
            fontSize: { xs: "1.05rem", sm: "1.25rem" },
            fontWeight: 700,
            color: brandColors.navy,
            mb: { xs: 1.5, sm: 2 },
            letterSpacing: "-0.02em",
          }}
        >
          Shop by Category
        </Typography>

        <Box
          ref={scrollRef}
          sx={{
            display: "flex",
            flexWrap: "nowrap",
            gap: { xs: 2, sm: 2.5 },
            overflowX: "auto",
            pb: 0.5,
            mx: { xs: -0.5, sm: 0 },
            px: { xs: 0.5, sm: 0 },
            scrollSnapType: "x mandatory",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
            WebkitOverflowScrolling: "touch",
          }}
        >
          {stripItems.map((item) => {
            const isSelected =
              selectedCategory === item.name ||
              (selectedCategory &&
                item.name !== ALL_CATEGORIES_LABEL &&
                selectedCategory.toLowerCase() === item.name.toLowerCase());
            const count =
              item.name === ALL_CATEGORIES_LABEL
                ? Object.values(itemCountByCategory).reduce((a, b) => a + b, 0)
                : itemCountByCategory[item.name] || 0;
            const hasProducts = count > 0;
            const useLocalOnly = brokenImages[item.name];
            const imageSrc = useLocalOnly
              ? getLocalCategoryImageSrc(item.name, categoryImageOverrides)
              : getCategoryImageSrc(
                  item.name,
                  categoryImageOverrides,
                  salesbeezImages
                );
            const showIconFallback = !imageSrc;

            return (
              <Box
                key={item.name}
                ref={isSelected ? selectedRef : null}
                component="button"
                type="button"
                onClick={() => onSelectCategory(item.name)}
                sx={{
                  flex: "0 0 auto",
                  scrollSnapAlign: "start",
                  cursor: "pointer",
                  border: "none",
                  background: "transparent",
                  p: 0,
                  width: { xs: 84, sm: 132 },
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: { xs: 1, sm: 1.25 },
                  transform: isSelected ? "scale(1.02)" : "none",
                  transition: "transform 0.2s ease",
                  "&:active": { transform: "scale(0.97)" },
                  "&:focus-visible": {
                    outline: `2px solid ${brandColors.gold}`,
                    outlineOffset: 4,
                    borderRadius: 2,
                  },
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    width: { xs: 80, sm: 126 },
                    height: { xs: 80, sm: 126 },
                    borderRadius: { xs: "16px", sm: "24px" },
                    border: isSelected
                      ? `2.5px solid ${brandColors.gold}`
                      : "1.5px solid rgba(11, 28, 72, 0.12)",
                    bgcolor: TILE_BG,
                    boxShadow: isSelected
                      ? `0 8px 24px rgba(11, 28, 72, 0.14), 0 0 0 1px ${brandColors.gold}44`
                      : "0 4px 10px -4px rgba(11, 28, 72, 0.12)",
                    overflow: "hidden",
                    transition: "box-shadow 0.25s ease, border-color 0.25s ease",
                    "&:hover img": {
                      transform: "scale(1.1)",
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={imageSrc}
                    alt={item.shortLabel}
                    loading="lazy"
                    decoding="async"
                    onError={() => {
                      if (!useLocalOnly) handleImageError(item.name);
                    }}
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      p: { xs: 0.75, sm: 1 },
                      transition: "transform 0.35s ease",
                      display: "block",
                      bgcolor: TILE_BG,
                    }}
                  />

                  {!hasProducts && item.name !== ALL_CATEGORIES_LABEL && (
                    <Chip
                      label="Soon"
                      size="small"
                      sx={{
                        position: "absolute",
                        top: 6,
                        right: 6,
                        height: 20,
                        fontSize: "0.65rem",
                        fontWeight: 700,
                        bgcolor: "rgba(255,255,255,0.92)",
                        color: primaryBlue?.[700] || brandColors.navyDark,
                        boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                        "& .MuiChip-label": { px: 0.75 },
                      }}
                    />
                  )}
                </Box>

                <Typography
                  component="span"
                  sx={{
                    fontSize: { xs: "12px", sm: "14px" },
                    fontWeight: isSelected ? 700 : 500,
                    color: isSelected
                      ? brandColors.navyDark
                      : "rgba(11, 28, 72, 0.82)",
                    textAlign: "center",
                    lineHeight: 1.25,
                    maxWidth: "100%",
                    px: 0.25,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {item.shortLabel}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default CategoryIconStrip;
